// /staff — list + revoke staff_users rows. Invite is deferred (501).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useApiClient, useStaffMe } from '../../lib/providers.js';
import {
  STAFF_QUERY_KEY,
  platformApi,
  type StaffRow,
} from '../../lib/api/platform.js';
import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { formatDate, formatDateTime } from '../../lib/format-date.js';

export function StaffPage() {
  const client = useApiClient();
  const me = useStaffMe();
  const query = useQuery({
    queryKey: STAFF_QUERY_KEY,
    queryFn: () => platformApi.listStaff(client),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Staff"
        description="Cortex operators with admin access. The staff_users row + Clerk publicMetadata.staffScopes drive scope checks."
      />

      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300">
        <strong>Invite is deferred:</strong> the Clerk Backend SDK integration
        for inviting new staff lands in a follow-up. Bootstrap new staff via{' '}
        <code>psql</code> + Clerk Dashboard until then; the Invite button below
        opens the same explanation in a dialog.
      </div>

      <div className="flex justify-end">
        <InviteButton />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Staff list</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {query.isPending ? (
            <SkeletonRows />
          ) : query.isError ? (
            <div className="px-4">
              <ErrorRetry
                title="Failed to load staff list"
                error={query.error}
                onRetry={() => {
                  void query.refetch();
                }}
              />
            </div>
          ) : query.data.rows.length === 0 ? (
            <div className="px-4">
              <EmptyState
                title="No staff registered"
                description="The first staff user is provisioned via psql per the runbook."
              />
            </div>
          ) : (
            <StaffTable rows={query.data.rows} myClerkId={me.staffId} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StaffTable({ rows, myClerkId }: { rows: readonly StaffRow[]; myClerkId: string }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Email</TableHead>
          <TableHead>Clerk id</TableHead>
          <TableHead>Scopes</TableHead>
          <TableHead>Invited</TableHead>
          <TableHead>Last active</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.clerkUserId}>
            <TableCell>
              {row.email}
              {row.clerkUserId === myClerkId && (
                <Badge variant="outline" className="ml-2">
                  you
                </Badge>
              )}
            </TableCell>
            <TableCell>
              <code className="text-xs">{row.clerkUserId}</code>
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {row.scopes.map((s) => (
                  <Badge key={s} variant="secondary">
                    {s}
                  </Badge>
                ))}
              </div>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {formatDate(row.invitedAt)}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {row.lastActiveAt ? formatDateTime(row.lastActiveAt) : 'Never'}
            </TableCell>
            <TableCell className="text-right">
              <RemoveButton row={row} self={row.clerkUserId === myClerkId} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RemoveButton({ row, self }: { row: StaffRow; self: boolean }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const mut = useMutation({
    mutationFn: () => platformApi.deleteStaff(client, row.clerkUserId),
    onSuccess: () => {
      toast.success(self ? 'Removed yourself' : `Removed ${row.email}`);
      void qc.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
      setOpen(false);
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Remove failed');
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Trash2 aria-hidden className="mr-1 h-3.5 w-3.5" />
        Remove
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove staff?</DialogTitle>
          <DialogDescription>
            Deletes the <code>staff_users</code> row for {row.email}. Their Clerk
            account remains, but they lose admin access immediately. Restoring
            requires re-bootstrapping via <code>psql</code>.
            {self && (
              <span className="mt-2 block font-semibold text-destructive">
                Self-removal: you will be locked out of admin until a peer re-adds
                you.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={mut.isPending}
            onClick={() => {
              mut.mutate();
            }}
          >
            {mut.isPending ? 'Removing…' : self ? 'Remove me' : `Remove ${row.email}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteButton() {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="default"
        onClick={() => {
          setOpen(true);
        }}
      >
        <UserPlus aria-hidden className="mr-1 h-3.5 w-3.5" />
        Invite staff
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite is deferred</DialogTitle>
          <DialogDescription>
            Wiring the Clerk Backend SDK to invite + set publicMetadata is a
            P6 follow-up. Until then:
          </DialogDescription>
        </DialogHeader>
        <ol className="ml-4 list-decimal space-y-2 text-sm text-muted-foreground">
          <li>Have the new staff member sign up via Clerk normally.</li>
          <li>
            In the Clerk Dashboard, set their{' '}
            <code className="text-xs">publicMetadata.staffScopes</code> to the
            scope array (e.g.{' '}
            <code className="text-xs">[&quot;staff:read&quot;]</code>).
          </li>
          <li>
            Insert a row in <code className="text-xs">staff_users</code> via{' '}
            <code className="text-xs">psql</code>:
            <pre className="mt-1 overflow-auto rounded-md bg-muted/30 p-2 text-xs">
{`INSERT INTO staff_users (clerk_user_id, email, scopes)
VALUES ('user_xxx', 'name@example.com', ARRAY['staff:read']);`}
            </pre>
          </li>
        </ol>
        <DialogFooter>
          <Button
            onClick={() => {
              setOpen(false);
            }}
          >
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2 px-4 py-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}
